import * as React from "react";
import { GridToolbarContainer } from "@mui/x-data-grid-pro";
import { List, ListItem, ListItemText, Popover, Switch } from "@mui/material";
import { Box } from "@mui/system";
import { InstrumentVisibility } from "./assessmentStatusDataGrid";
import { right } from "@popperjs/core";
import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import CclButton from "../../../../../../components/common/cclButtons/cclButton";
import CclActionButton, {
    CclSelectOption,
} from "../../../../../../components/common/cclActionButton/cclActionButton";
import CclLoadingButton from "../../../../../../components/common/cclButtons/cclLoadingButton";

export interface AssessmentStatusDataGridToolbarProps {
    scoringMode: "none" | "request" | "score";
    instrumentVisibility: InstrumentVisibility[];
    numSelected: number;
    handleButtonClick: (button: string) => void;
    onAssessmentFilterChange: (visible: boolean, instrumentId: number) => void;
    sessionCode: string;
    sessionKey: number;
    isRegistration: boolean;
    inProgressEvent: string | null;
    hideRaterActions: boolean;
    showExport: boolean;
    hideParticipantEmailActions: boolean;
    showSendParticipantsEmail: boolean;
}

const AssessmentStatusDataGridToolbar: React.FC<AssessmentStatusDataGridToolbarProps> = (props) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const toggleVisibility = (visible: boolean, instrumentId: number) => {
        props.onAssessmentFilterChange(visible, instrumentId);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openCclScoring = (evt: React.MouseEvent<HTMLButtonElement>) => {
        props.handleButtonClick("viewscoring");
    };

    const openCclRequestScoring = (evt: React.MouseEvent<HTMLButtonElement>) => {
        props.handleButtonClick("requestscoring");
    };

    const showLegend = () => {
        props.handleButtonClick("showlegend");
    };

    const assessmentChecklistOpen = Boolean(anchorEl);
    const id = assessmentChecklistOpen ? "assessment-visiblity-checkbox-popover" : undefined;

    var actionOptions: CclSelectOption[] = [
        {
            val: "createjournalentry",
            option: "Create Journal Entry",
            disabled: props.numSelected === 0,
        },
    ];

    if (!props.hideParticipantEmailActions) {
        actionOptions.push({
            val: "resendinvitations",
            option: "Resend Invitation",
            disabled: props.numSelected === 0,
        });
        actionOptions.push({
            val: "sendreminders",
            option: "Resend Reminder",
            disabled: props.numSelected === 0,
        });
    }

    if (!props.hideRaterActions) {
        actionOptions.push({
            val: "resendraterinvitations",
            option: "Resend Rater Invitations",
            disabled: props.numSelected === 0,
        });
        actionOptions.push({
            val: "sendraterreminders",
            option: "Resend Rater Reminders",
            disabled: props.numSelected === 0,
        });
    }

    if (props.showSendParticipantsEmail) {
        actionOptions.push({
            val: "sendparticipantsemail",
            option: "Send Participant Emails",
            disabled: props.numSelected === 0,
        });
    }

    const CclToolbarBtnSx = {
        paddingLeft: "10px",
        paddingRight: "10px",
        paddingTop: "4px",
        paddingBottom: "4px",
        margin: "5px",
        fontSize: "12px",
        lineHeight: "21px",
    };

    return (
        <GridToolbarContainer>
            <Box sx={{ width: 1, justifyContent: "space-between", padding: 2 }} padding={1}>
                {props.scoringMode === "request" ? (
                    <CclButton
                        size="small"
                        mode={"primary"}
                        disabled={props.numSelected === 0}
                        onClick={openCclRequestScoring}
                        sx={CclToolbarBtnSx}
                    >
                        Request Scoring
                    </CclButton>
                ) : null}
                {props.scoringMode === "score" ? (
                    <CclButton
                        size="small"
                        mode={"primary"}
                        onClick={openCclScoring}
                        sx={CclToolbarBtnSx}
                    >
                        Score Assessments
                    </CclButton>
                ) : null}
                {props.showExport ? (
                    <>
                        <CclLoadingButton
                            size="small"
                            onClick={() => props.handleButtonClick("excelExport")}
                            sx={{
                                ...CclToolbarBtnSx,
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                flexDirection: "row",
                            }}
                            mode={"primary"}
                            loading={props.inProgressEvent === "excelExport"}
                            disabled={props.inProgressEvent === "excelExport"}
                        >
                            Export To Excel
                        </CclLoadingButton>
                        <CclButton
                            size="small"
                            onClick={() => props.handleButtonClick("export")}
                            sx={{
                                ...CclToolbarBtnSx,
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                flexDirection: "row",
                            }}
                            mode={"primary"}
                        >
                            Export To PDF
                        </CclButton>
                    </>
                ) : null}
                {props.isRegistration ? null : (
                    <CclActionButton
                        disabled={props.numSelected === 0}
                        fixedLabel="Multi-Participant Actions"
                        options={actionOptions}
                        sx={CclToolbarBtnSx}
                        onSelect={async (val: string) => props.handleButtonClick(val)}
                    />
                )}
                <CclButton
                    size="small"
                    onClick={showLegend}
                    sx={{
                        ...CclToolbarBtnSx,
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        flexDirection: "row",
                    }}
                    mode={"secondary"}
                >
                    How To Interpret Status?
                </CclButton>
                {props.instrumentVisibility.length > 0 ? (
                    <React.Fragment>
                        <CclButton
                            size="small"
                            onClick={handleClick}
                            endIcon={assessmentChecklistOpen ? <ArrowDropDown /> : <ArrowRight />}
                            sx={{
                                ...CclToolbarBtnSx,
                                float: right,
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                flexDirection: "row",
                                marginLeft: 3,
                            }}
                            mode={"secondary"}
                        >
                            Filter Assessments
                        </CclButton>
                        <Popover
                            id={id}
                            open={assessmentChecklistOpen}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                        >
                            <List dense>
                                {props.instrumentVisibility
                                    .sort((a: InstrumentVisibility, b: InstrumentVisibility) =>
                                        a.name > b.name ? 1 : -1,
                                    )
                                    .map((iv) => (
                                        <ListItem key={iv.id} value={iv.id}>
                                            <Switch
                                                checked={iv.visible}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>,
                                                    checked: boolean,
                                                ) => toggleVisibility(checked, iv.id)}
                                                size="small"
                                                sx={{ p: "5px", mr: "4px" }}
                                            />
                                            <ListItemText primary={iv.name} />
                                        </ListItem>
                                    ))}
                            </List>
                        </Popover>
                    </React.Fragment>
                ) : null}
            </Box>
        </GridToolbarContainer>
    );
};

export default AssessmentStatusDataGridToolbar;
